import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2, Quote } from "lucide-react";
import api from "@/lib/api";
import { StarRating } from "@/components/StarRating";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });

export default function Reviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/reviews").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div data-testid="reviews-page">
      <section className="bubble-bg noise-overlay">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center text-white sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Ce spun clienții noștri</h1>
          <div className="mt-5 flex flex-col items-center gap-2">
            <div className="flex items-center gap-3">
              <span className="font-heading text-5xl font-extrabold" data-testid="all-reviews-average">{data.average}</span>
              <StarRating value={data.average} size={24} />
            </div>
            <p className="text-white/80" data-testid="all-reviews-count">Bazat pe {data.count} recenzii verificate</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-5 sm:grid-cols-2" data-testid="all-reviews-list">
          {data.reviews.map((r) => (
            <div key={r.id} className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`all-review-${r.id}`}>
              <Quote className="absolute right-5 top-5 h-6 w-6 text-slate-100" />
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 font-heading font-bold text-primary">
                  {r.author.charAt(0).toUpperCase()}
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{r.author}</span>
                    {r.verified && <CheckCircle2 className="h-4 w-4 text-emerald-600" title="Cumpărător verificat" />}
                  </div>
                  <StarRating value={r.rating} size={14} className="mt-0.5" />
                </div>
              </div>
              <p className="mt-4 leading-relaxed text-slate-700">{r.comment}</p>
              {r.video_url && (
                <video
                  className="mt-3 w-full max-w-[280px] rounded-xl border border-slate-200 bg-black"
                  muted
                  autoPlay
                  loop
                  playsInline
                  controls
                  preload="auto"
                  data-testid={`all-review-video-${r.id}`}
                >
                  {r.video_url_webm && <source src={r.video_url_webm} type="video/webm" />}
                  <source src={r.video_url} type="video/mp4" />
                </video>
              )}
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                <Link to={`/produs/${r.product_id}`} className="line-clamp-1 text-sm font-medium text-primary hover:underline" data-testid={`all-review-product-${r.id}`}>
                  {r.product_name}
                </Link>
                <span className="shrink-0 text-xs text-slate-400">{fmtDate(r.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
        {data.reviews.length === 0 && (
          <p className="py-16 text-center text-slate-500">Încă nu există recenzii.</p>
        )}
      </section>
    </div>
  );
}
